<template>
  <BaseModal :is-open="isVisible" :title="title" @close="closeModal">
    <template #body>
      <div class="mb-6 text-center">
        <div class="mb-3">
          <span class="text-3xl">💼</span>
        </div>
        <!-- Title is now a prop of BaseModal, message shown here -->
        <p class="text-gray-600 text-sm leading-relaxed">
          {{ message }}
        </p>
      </div>

      <div class="mb-6 space-y-2">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">
          Benefits of Creating Your Account:
        </h4>
        <div class="space-y-2">
          <div class="flex items-center text-sm text-gray-600">
            <svg
              class="w-4 h-4 text-green-600 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            Save your tax calculations & proposals
          </div>
          <div class="flex items-center text-sm text-gray-600">
            <svg
              class="w-4 h-4 text-green-600 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            KRA-compliant invoicing templates
          </div>
          <div class="flex items-center text-sm text-gray-600">
            <svg
              class="w-4 h-4 text-green-600 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            Access from anywhere in Kenya
          </div>
          <div class="flex items-center text-sm text-gray-600">
            <svg
              class="w-4 h-4 text-green-600 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            Support for Kenyan businesses
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="space-y-3 w-full">
        <button
          @click="navigateToAuth"
          class="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <span class="flex items-center justify-center">
            <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            Sign In / Create Account
          </span>
        </button>
        <button
          @click="continueWithoutSaving"
          class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-all duration-200 border border-gray-300"
        >
          Continue Without Saving
        </button>
      </div>
      <div class="mt-6 pt-4 border-t border-gray-100 w-full">
        <p class="text-center text-xs text-gray-500">
          🇰🇪 Proudly Made in Kenya | Tuungane Business Solutions
        </p>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import BaseModal from "@/components/common/BaseModal.vue"; // Import BaseModal
import { defineEmits, defineProps } from "vue";

// Props
const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Sign In to Save Your Work",
  },
  message: {
    type: String,
    default:
      "Join thousands of Kenyan entrepreneurs who trust our platform to manage their business calculations and stay KRA-compliant.",
  },
  returnUrl: {
    type: String,
    default: null,
  },
});

// Emits
const emit = defineEmits([
  "close",
  "authRedirect",
  "continueWithoutSaving",
  "navigate-auth",
  "continue-no-save",
]);

// Methods
const closeModal = () => {
  emit("close");
};

const navigateToAuth = () => {
  emit("authRedirect", props.returnUrl);
  emit("navigate-auth", props.returnUrl);
};

const continueWithoutSaving = () => {
  emit("continueWithoutSaving");
  emit("continue-no-save");
};
</script>

<style scoped>
/* Custom scrollbar for modal content */
.modal-content::-webkit-scrollbar {
  width: 4px;
}

.modal-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #10b981;
  border-radius: 2px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: #059669;
}
</style>
